<template>
  <q-dialog v-model="dialogAberto" persistent :maximized="mobile">
    <q-card
      v-if="campanha && estado === 'pergunta'"
      class="campanha-card"
      :class="{ 'campanha-card--mobile': mobile, 'campanha-card--solido': usandoFundoSolido }"
      :style="estiloDoCard"
    >
      <div class="campanha-card__corpo">
        <div class="campanha-card__conteudo">
          <div class="campanha-card__titulo" :style="estiloDoTexto">{{ campanha.titulo }}</div>
          <div class="campanha-card__texto q-mt-sm" :style="estiloDoTexto">{{ textoPersonalizado }}</div>
        </div>
        <div v-if="videoEmbed" class="campanha-card__video">
          <video-campanha :key="campanha.id" :src="videoEmbed" @liberar="videoLiberado = true" />
        </div>
      </div>

      <div v-if="aguardandoVideo" class="campanha-card__aviso-video" :style="estiloDoTexto">
        Assista ao vídeo até o fim para responder.
      </div>

      <div class="campanha-card__rodape">
        <q-btn
          v-for="opcao in campanha.opcoes"
          :key="opcao.id"
          unelevated
          :label="opcao.rotulo"
          :disable="opcaoBloqueada(opcao) || enviando || aguardandoVideo"
          :style="estiloDoBotao(opcao)"
          @click="responder(opcao)"
        />
      </div>
    </q-card>

    <q-card v-else-if="campanha && estado === 'ja_respondida'" style="width: 100%; max-width: 560px;">
      <q-card-section>
        <div class="text-h6">Campanha já respondida</div>
        <div class="q-mt-sm">
          Outro usuário da sua empresa já registrou uma resposta para esta campanha.
        </div>
      </q-card-section>

      <q-card-actions align="right" class="q-px-md q-pb-md">
        <q-btn label="Fechar" color="primary" @click="fechar()" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import VideoCampanha from "@/components/Utils/video-campanha.vue";

export const POLL_INTERVALO_MS = 25000;

// Descoberta de campanha nova para quem ja esta logado. Intervalo mais folgado
// que o de status porque cada chamada recalcula elegibilidade no servidor.
export const POLL_DESCOBERTA_MS = 60000;

// Trocar de tela e o gatilho principal da descoberta — e a coisa que o usuario
// mais faz, e esperar ate um minuto pelo poll fazia a campanha "nao aparecer"
// pra quem ja estava logado. A janela existe porque cada consulta recalcula
// elegibilidade no servidor: sem ela, clicar rapido entre telas viraria uma
// consulta por clique, vezes todo mundo logado.
export const DESCOBERTA_THROTTLE_MS = 15000;

// Precisam bater com os padrões mostrados no painel admin
// (src/Template/Campanhas/index.ctp, COR_PADRAO_*).
export const COR_PADRAO_FUNDO = "#f9b500";
export const COR_PADRAO_TEXTO = "#ffffff";
export const COR_BLOQUEADA = "#d3d3d3";
export const COR_AZUL_DOURASOFT = "#2741b5";
// No celular sem arte o card inteiro fica amarelo — e é por isso que o botão
// padrão troca para o azul ali: amarelo sobre amarelo desaparece.
export const COR_FUNDO_SOLIDO_MOBILE = COR_PADRAO_FUNDO;
export const LARGURA_MOBILE_PX = 600;

// O servidor só grava embed do Gumlet (App\Module\Campanhas\VideoGumlet);
// conferir de novo aqui custa uma linha e impede que um dado torto vire
// iframe de outro site dentro do app.
const PADRAO_EMBED_GUMLET = /^https:\/\/play\.gumlet\.io\/embed\/[A-Za-z0-9]{8,64}$/;

export default {
  name: "modal-campanha",
  components: { VideoCampanha },
  data() {
    return {
      estado: "pergunta",
      enviando: false,
      polling: null,
      pollingDescoberta: null,
      ultimaDescobertaEm: 0,
      mobile: false,
      videoLiberado: false,
    };
  },
  computed: {
    ...mapGetters(["campanhaPendente", "dataUser"]),
    campanha() {
      return this.campanhaPendente;
    },
    videoEmbed() {
      const url = this.campanha && this.campanha.video_url;
      return url && PADRAO_EMBED_GUMLET.test(url) ? url : null;
    },
    // Com vídeo, nenhum botão responde até ele terminar (ou até o player
    // desistir de carregar — ver TEMPO_LIMITE_PRONTO_MS no video-campanha).
    aguardandoVideo() {
      return !!this.videoEmbed && !this.videoLiberado;
    },
    temOpcoesValidas() {
      return !!this.campanha && Array.isArray(this.campanha.opcoes) && this.campanha.opcoes.length > 0;
    },
    dialogAberto() {
      if (!this.campanha) {
        return false;
      }
      // Campanha malformada (sem opções) nunca deve prender o usuário num
      // dialog persistent sem nenhum botão — só "já respondida" (que sempre
      // tem o botão Fechar) pode abrir sem opções válidas.
      return this.estado === "ja_respondida" || this.temOpcoesValidas;
    },
    primeiroNome() {
      const nome = (this.dataUser && this.dataUser.nome) || "";
      return nome.split(" ")[0];
    },
    textoPersonalizado() {
      if (!this.campanha) {
        return "";
      }
      const texto = this.campanha.texto || "";
      return texto.replace(/\[primeiro_nome\]/g, this.primeiroNome);
    },
    // No celular a arte de desktop NUNCA é reaproveitada: ela é horizontal e
    // desenhada com o conteúdo à esquerda, então numa tela estreita o `cover`
    // cortava o miolo e jogava o texto por cima da ilustração. Sem arte de
    // celular o modal vai para o fundo sólido, que é sempre legível.
    arteDeFundo() {
      if (!this.campanha) {
        return null;
      }
      const arte = this.mobile ? this.campanha.imagem_url_mobile : this.campanha.imagem_url;
      return arte || null;
    },
    usandoFundoSolido() {
      return !this.arteDeFundo;
    },
    // Só o fundo sólido DO CELULAR é amarelo. No desktop sem arte o card segue
    // azul, como sempre foi — e é o amarelo que obriga a inverter o botão.
    fundoAmarelo() {
      return this.mobile && this.usandoFundoSolido;
    },
    estiloDoCard() {
      if (this.arteDeFundo) {
        return { backgroundImage: `url(${this.arteDeFundo})` };
      }
      return { backgroundColor: this.fundoAmarelo ? COR_FUNDO_SOLIDO_MOBILE : COR_AZUL_DOURASOFT };
    },
    corDoTexto() {
      return this.fundoAmarelo ? "#000000" : "#ffffff";
    },
    estiloDoTexto() {
      return {
        color: this.corDoTexto,
        // Sobre foto o texto precisa da sombra para não sumir nas áreas claras;
        // sobre cor chapada ela só sujaria o traço.
        textShadow: this.arteDeFundo ? "0 1px 3px rgba(0, 0, 0, 0.55)" : "none",
      };
    },
  },
  watch: {
    $route() {
      this.descobrirPorNavegacao();
    },
    campanha(novo, antigo) {
      if (!novo) {
        this.pararPolling();
        this.iniciarPollingDescoberta();
        return;
      }
      const trocouCampanha = !antigo || novo.id !== antigo.id;
      if (trocouCampanha) {
        this.pararPollingDescoberta();
        this.estado = "pergunta";
        this.videoLiberado = false;
        this.iniciarPolling();
      }
    },
  },
  mounted() {
    this.atualizarLargura();
    window.addEventListener("resize", this.atualizarLargura);

    if (this.campanha) {
      this.iniciarPolling();
      return;
    }
    this.iniciarPollingDescoberta();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.atualizarLargura);
    this.pararPolling();
    this.pararPollingDescoberta();
  },
  methods: {
    estiloDoBotao(opcao) {
      // Esperando o vídeo, todos ficam cinza como a opção bloqueada: só a
      // opacidade do disable deixava o amarelo com cara de botão ativo. Ao
      // liberar, cada um volta para a cor que teria sem vídeo.
      if (this.opcaoBloqueada(opcao) || this.aguardandoVideo) {
        return { backgroundColor: COR_BLOQUEADA, color: COR_PADRAO_TEXTO };
      }

      // Cor escolhida à mão vale em qualquer tela — a inversão para azul é uma
      // correção do PADRÃO sobre o fundo amarelo, não uma regra de celular.
      const corEscolhida = opcao.cor_fundo || null;
      const fundoPadrao = this.fundoAmarelo ? COR_AZUL_DOURASOFT : COR_PADRAO_FUNDO;

      return {
        backgroundColor: corEscolhida || fundoPadrao,
        color: opcao.cor_texto || COR_PADRAO_TEXTO,
      };
    },
    atualizarLargura() {
      this.mobile = window.innerWidth <= LARGURA_MOBILE_PX;
    },
    opcaoBloqueada(opcao) {
      // Flag calculada e autoritativa do servidor (ElegibilidadeService) —
      // o payload de campanha_pendente nunca envia aplicativo_id, só o
      // resultado já computado. O servidor também recusa no responder()
      // mesmo que esta checagem falhe (defesa em profundidade real).
      return !!opcao.bloqueada;
    },
    iniciarPolling() {
      this.pararPolling();
      this.polling = setInterval(() => {
        this.verificarStatus();
      }, POLL_INTERVALO_MS);
    },
    pararPolling() {
      if (this.polling) {
        clearInterval(this.polling);
        this.polling = null;
      }
    },
    iniciarPollingDescoberta() {
      this.pararPollingDescoberta();
      this.pollingDescoberta = setInterval(() => {
        this.descobrirCampanha();
      }, POLL_DESCOBERTA_MS);
    },
    pararPollingDescoberta() {
      if (this.pollingDescoberta) {
        clearInterval(this.pollingDescoberta);
        this.pollingDescoberta = null;
      }
    },
    async descobrirCampanha() {
      if (this.campanha) {
        return;
      }
      // Marcado antes do await: duas navegacoes quase simultaneas nao podem
      // passar as duas pela janela enquanto a primeira ainda esta no ar.
      this.ultimaDescobertaEm = Date.now();
      // A action comita campanhaPendente quando acha algo; o watch de
      // `campanha` cuida de trocar os pollings a partir dai.
      await this.$store.dispatch("buscarCampanhaPendente");
    },
    async descobrirPorNavegacao() {
      if (this.campanha) {
        return;
      }
      if (Date.now() - this.ultimaDescobertaEm < DESCOBERTA_THROTTLE_MS) {
        return;
      }
      await this.descobrirCampanha();
    },
    async verificarStatus() {
      if (!this.campanha) {
        return;
      }
      const res = await this.$store.dispatch("buscarStatusCampanha", this.campanha.id);
      if (res.ok && res.respondida) {
        this.estado = "ja_respondida";
        this.pararPolling();
      }
    },
    async responder(opcao) {
      if (this.opcaoBloqueada(opcao) || this.enviando || this.aguardandoVideo || !this.campanha) {
        return;
      }
      this.enviando = true;

      // Abre a aba (about:blank) ainda dentro do gesto síncrono do clique —
      // abrir só depois do await da resposta faz vários navegadores
      // (Safari, Firefox) bloquearem como pop-up. Passar "noopener" nas
      // features faz window.open() retornar null (spec WHATWG) — a mesma
      // proteção contra reverse tabnabbing vem de zerar .opener no handle
      // que a chamada devolve.
      let novaAba = null;
      if (opcao.link_redirecionamento) {
        novaAba = window.open("", "_blank");
        if (novaAba) {
          novaAba.opener = null;
        }
      }

      const res = await this.$store.dispatch("responderCampanha", {
        campanhaId: this.campanha.id,
        opcaoId: opcao.id,
      });
      this.enviando = false;

      if (res.ok) {
        if (novaAba && res.opcao && res.opcao.link_redirecionamento) {
          novaAba.location = res.opcao.link_redirecionamento;
        } else if (novaAba) {
          novaAba.close();
        }
        this.estado = "pergunta";
        return;
      }

      if (novaAba) {
        novaAba.close();
      }

      if (res.jaRespondida) {
        this.estado = "ja_respondida";
        this.pararPolling();
        return;
      }

      this.$q.notify({
        message: "Não foi possível registrar sua resposta. Tente novamente.",
        color: "red",
        position: "top-right",
      });
    },
    fechar() {
      this.pararPolling();
      this.$store.commit("setCampanhaPendente", null);
    },
  },
};
</script>

<style lang="stylus" scoped>
.campanha-card
  width 100%
  max-width 900px
  min-height 420px
  background-size cover
  background-position center
  background-color #2741b5
  display flex
  flex-direction column

.campanha-card__corpo
  flex 1
  display flex
  align-items center

.campanha-card__conteudo
  width 50%
  padding 40px 40px 0
  box-sizing border-box

.campanha-card__titulo
  font-size 2rem
  font-weight 700
  line-height 1.2

.campanha-card__texto
  white-space pre-line

// Metade direita do card, no lugar da ilustração da arte.
.campanha-card__video
  width 50%
  padding 40px 40px 0 0
  box-sizing border-box

.campanha-card__aviso-video
  padding 16px 40px 0
  font-size 0.9rem

// Botoes alinhados a esquerda, na ordem em que o admin cadastrou. Antes eles
// eram espalhados pela largura toda (primeiro na ponta esquerda, ultimo na
// direita), o que separava visualmente opcoes que sao uma escolha so.
.campanha-card__rodape
  display flex
  flex-wrap wrap
  justify-content flex-start
  align-items center
  padding 24px 40px 32px
  gap 12px

// No celular o dialog e maximized: o conteudo ocupa a tela inteira e a coluna
// de 50% (que existe para nao cobrir a ilustracao da arte desktop) deixa de
// fazer sentido.
.campanha-card--mobile
  max-width none
  min-height 100%
  border-radius 0

  .campanha-card__conteudo
    width 100%
    padding 32px 20px 0

  .campanha-card__titulo
    font-size 1.75rem

  .campanha-card__corpo
    flex-direction column
    align-items stretch

  .campanha-card__video
    width 100%
    padding 20px 20px 0

  .campanha-card__aviso-video
    padding 16px 20px 0

  .campanha-card__rodape
    padding 24px 20px 32px

    > *
      width 100%

// Sem arte, o card e uma cor chapada: o texto sobe pro topo em vez de ficar
// centralizado num vazio.
.campanha-card--solido.campanha-card--mobile
  .campanha-card__corpo
    align-items flex-start
</style>
